import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Stack, Typography, Chip, Button, IconButton, Skeleton, Divider, Tooltip,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useSnackbar } from 'notistack';
import { fetchStatutoryConfig, deleteStatutoryConfig, fetchRecords } from '../services/api';
import StatutoryConfigDialog from './StatutoryConfigDialog';

// Component code → LDG applicability column.
// LWF has no LDG flag in the current schema and is therefore excluded under
// strict filtering (only components with an explicit LDG flag = 'Y' are shown).
const LDG_FLAG_BY_CODE = {
  EPF:      'pf_applicable',
  ESI:      'esi_applicable',
  PT:       'pt_applicable',
  TDS:      'tds_applicable',
  GRATUITY: 'gratuity_applicable',
  BONUS:    'bonus_applicable',
};

// Component code → field name in configuration_json that holds the "registration code".
const REG_CODE_FIELD = {
  EPF:      'epf_establishment_code',
  ESI:      'esi_employer_code',
  PT:       'pt_registration_number',
  TDS:      'tan_number',
  GRATUITY: null,
  BONUS:    null,
  LWF:      'lwf_state',
};

export default function CompanyStatutoryPanel({ companyId }) {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading]               = useState(true);
  const [components, setComponents]         = useState([]);
  const [configurations, setConfigurations] = useState([]);
  const [ldgs, setLdgs]                     = useState([]);
  const [configuring, setConfiguring]       = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cfgRes, ldgRes] = await Promise.all([
        fetchStatutoryConfig(companyId),
        fetchRecords('LEGISLATION_DATA_GROUP'),
      ]);
      setComponents(cfgRes.data.data.components || []);
      setConfigurations(cfgRes.data.data.configurations || []);
      setLdgs(ldgRes.data.data || []);
    } catch (e) {
      enqueueSnackbar('Failed to load statutory data: ' + (e.response?.data?.message || e.message), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [companyId, enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);

  // Build a union flag map across all LDGs: column → true if any LDG has 'Y'.
  const ldgUnion = {};
  for (const l of ldgs) {
    for (const k of Object.keys(l)) {
      if (l[k] === 'Y') ldgUnion[k] = true;
    }
  }

  const applicable = components.filter(c => {
    const flag = LDG_FLAG_BY_CODE[c.code];
    return flag && ldgUnion[flag] === true;
  });

  const configByCode = Object.fromEntries(configurations.map(c => [c.component_code, c]));
  const configuredCount = applicable.reduce((n, c) => n + (configByCode[c.code] ? 1 : 0), 0);

  const handleRemove = async (code, name) => {
    try {
      await deleteStatutoryConfig(companyId, code);
      enqueueSnackbar(`${name} configuration removed`, { variant: 'success' });
      load();
    } catch (e) {
      enqueueSnackbar('Error: ' + (e.response?.data?.message || e.message), { variant: 'error' });
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 2.5, bgcolor: '#FAFBFC' }}>
        <Skeleton height={32} width={260} sx={{ mb: 2 }} />
        {[...Array(3)].map((_, i) => <Skeleton key={i} height={56} sx={{ mb: 1, borderRadius: 1 }} />)}
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2.5, bgcolor: '#FAFBFC', borderTop: '1.5px solid #E5E7EB' }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={1.2}>
          <Typography fontWeight={700} fontSize={14} color="#111827">Statutory Components</Typography>
          <Chip
            label={`${configuredCount}/${applicable.length} Configured`}
            size="small"
            sx={{
              height: 22, fontSize: 11, fontWeight: 700, borderRadius: '8px',
              bgcolor: configuredCount === applicable.length && applicable.length > 0 ? '#dcfce7' : '#FEF3C7',
              color:   configuredCount === applicable.length && applicable.length > 0 ? '#15803d' : '#92400E',
              border:  configuredCount === applicable.length && applicable.length > 0 ? '1px solid #bbf7d0' : '1px solid #FDE68A',
            }}
          />
        </Stack>
        <Typography fontSize={11.5} color="text.secondary">
          Filtered by LDG applicability flags
        </Typography>
      </Stack>

      {applicable.length === 0 ? (
        <Box sx={{ py: 3, textAlign: 'center', bgcolor: 'white', borderRadius: '8px', border: '1.5px dashed #E5E7EB' }}>
          <Typography fontSize={13} color="text.secondary">
            No statutory components applicable — no LDG has any <code>_applicable</code> flag set to "Y".
          </Typography>
        </Box>
      ) : (
        <Box sx={{ bgcolor: 'white', borderRadius: '10px', border: '1.5px solid #E5E7EB', overflow: 'hidden' }}>
          {applicable.map((c, i) => {
            const cfg     = configByCode[c.code];
            const regField = REG_CODE_FIELD[c.code];
            const regCode  = regField ? cfg?.configuration_json?.[regField] : null;
            const configured = !!cfg;
            return (
              <React.Fragment key={c.code}>
                {i > 0 && <Divider />}
                <Stack
                  direction="row" alignItems="center" spacing={2}
                  sx={{ px: 2, py: 1.2, '&:hover': { bgcolor: '#F9FAFB' } }}
                >
                  {/* Status icon + name */}
                  <Stack direction="row" alignItems="center" spacing={1.2} sx={{ minWidth: 220 }}>
                    {configured
                      ? <CheckCircleIcon sx={{ fontSize: 18, color: '#22c55e' }} />
                      : <RadioButtonUncheckedIcon sx={{ fontSize: 18, color: '#94A3B8' }} />}
                    <Box>
                      <Typography fontSize={13} fontWeight={600} color="#111827" lineHeight={1.2}>{c.name}</Typography>
                      <Typography fontSize={10.5} color="#9CA3AF" sx={{ fontFamily: 'monospace', letterSpacing: 0.3 }}>{c.code}</Typography>
                    </Box>
                  </Stack>

                  {/* Registration code */}
                  <Box sx={{ minWidth: 200, flex: 1 }}>
                    <Typography fontSize={10.5} color="#9CA3AF" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Registration Code
                    </Typography>
                    <Typography fontSize={13} fontWeight={500} color={regCode ? '#374151' : '#D1D5DB'}>
                      {regCode || (regField ? '—' : 'N/A')}
                    </Typography>
                  </Box>

                  {/* Status chip */}
                  <Chip
                    label={configured ? 'Configured' : 'Not Configured'}
                    size="small"
                    sx={{
                      height: 22, fontSize: 11, fontWeight: 600, borderRadius: '20px',
                      bgcolor: configured ? '#dcfce7' : '#F3F4F6',
                      color:   configured ? '#16a34a' : '#6B7280',
                      border:  configured ? '1px solid #bbf7d0' : '1px solid #E5E7EB',
                    }}
                  />

                  {/* Actions */}
                  <Stack direction="row" spacing={0.8}>
                    <Button
                      size="small" variant={configured ? 'outlined' : 'contained'}
                      startIcon={<SettingsIcon sx={{ fontSize: 14 }} />}
                      onClick={() => setConfiguring(c)}
                      sx={{
                        fontSize: 12, py: 0.4, px: 1.4, minWidth: 0,
                        ...(configured ? {
                          borderColor: '#22c55e', color: '#16a34a',
                          '&:hover': { borderColor: '#16a34a', bgcolor: '#f0fdf4' },
                        } : {
                          background: 'linear-gradient(135deg,#16a34a,#22c55e)',
                          '&:hover': { background: 'linear-gradient(135deg,#15803d,#16a34a)' },
                        }),
                      }}
                    >
                      {configured ? 'Edit' : 'Configure'}
                    </Button>
                    {configured && (
                      <Tooltip title="Remove configuration" arrow>
                        <IconButton size="small" onClick={() => handleRemove(c.code, c.name)}
                          sx={{ color: '#EF4444', '&:hover': { bgcolor: '#FEF2F2' } }}>
                          <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </Stack>
              </React.Fragment>
            );
          })}
        </Box>
      )}

      <StatutoryConfigDialog
        open={!!configuring}
        onClose={() => setConfiguring(null)}
        companyId={companyId}
        component={configuring}
        existingConfig={configuring ? configByCode[configuring.code] : null}
        onSaved={load}
      />
    </Box>
  );
}
